// =============================================================================
// ui/state/useFreeTierCap.ts — "may this user create another one of these?"
// -----------------------------------------------------------------------------
// ── ⚠ THREE HOOKS, NOT ONE THAT TAKES A REGISTRY ──
//
// One hook would have to call `useAccounts()`, `usePatterns()` AND
// `useExitModels()` unconditionally — hooks cannot be called behind an `if`.
// All three THROW outside their provider by design, so the exit-model editor
// would suddenly require the accounts provider to render at all. That is a real
// coupling, not just a test inconvenience: a screen would start depending on
// data it does not show.
//
// So each hook reads exactly the provider its own registry lives in, and they
// share the counting rule below.
//
// ── ⚠ WHY THE COUNT COMES FROM THE PROVIDER AND NOT A FRESH READ ──
//
// All three providers are mounted app-wide inside the auth gate and every
// creation screen already holds the rows, so the count is free. A cap that
// issued its own COUNT query would be a request per modal open, and it could
// disagree with the list the user is looking at — a worse failure than being one
// row stale.
//
// ── 🚨 NULL IS NOT ZERO ──
//
// Each provider is null until its read lands, and stays null if it fails. That
// null is passed THROUGH to the rule, which allows on it deliberately. Coercing
// it to 0 here would look identical in the happy path and would mean a failed
// read reads as "you have none of these" — which happens to allow too, but by
// luck rather than by decision. The direction has to be chosen out loud.
//
// 🔴 AND NO TEST PROTECTS THAT DISTINCTION — MEASURED, NOT ASSUMED. A sabotage
// run replacing `: null` with `: 0` here left all 30 assertions GREEN, because
// both paths allow and nothing on screen shows `count` yet. So this is a
// correctness argument the suite cannot currently defend.
//
// ▶ It stops being unobservable the moment anything renders the count — a
// "2 of 3 patterns used" line would read "0 of 3" after a failed read, which is
// a confident wrong number rather than an absent one. Add the assertion in the
// same commit as that line.
// =============================================================================

import { useAccounts } from './accounts';
import { useDemo } from './demo';
import { useEntitlement } from './useEntitlement';
import { useExitModels } from './exitModels';
import { usePatterns } from './patterns';
import { freeTierBlocks, type FreeTierRegistry } from '../../selectors/freeTier';

export interface FreeTierCap {
  /** The refusal sentence, or null when the create may proceed. */
  blocked: string | null;
  /** How many rows count toward the cap, or null while unknown. */
  count: number | null;
}

/**
 * ⚠ Archived accounts and retired patterns COUNT. Archiving is de-clutter, not
 * deletion; a cap that ignored it would be an unlimited free tier for anyone who
 * archives.
 *
 * 🚨 DEMO ROWS DO NOT COUNT, and this paragraph used to say there were none to
 * exclude — true when it was written, false since #90. The sample book holds
 * FIVE accounts against a free limit of one, so without this a prospect who
 * loaded the demo would be told they had hit their account limit, on a screen
 * full of accounts that are not theirs and cannot be deleted.
 *
 * ⚠ Passing `null` rather than 0 is the deliberate spelling: null already means
 * "unknown, so allow", and it routes through the same branch the rule documents
 * instead of asserting the demo user owns nothing.
 *
 * ⚠ It is belt AND braces — nothing can be created in a demo anyway, because
 * every write throws (`io/demo/client.ts`). This is about what the screen SAYS,
 * which the write guard cannot fix.
 */
function capOf(
  registry: FreeTierRegistry, pro: boolean, rows: readonly unknown[] | null,
  demo: boolean,
): FreeTierCap {
  const count = demo || !rows ? null : rows.length;
  return { blocked: freeTierBlocks({ pro, registry, count }), count };
}

export function useAccountCap(): FreeTierCap {
  const { pro } = useEntitlement();
  const { accounts } = useAccounts();
  return capOf('accounts', pro, accounts, useDemo().active);
}

export function usePatternCap(): FreeTierCap {
  const { pro } = useEntitlement();
  const { patterns } = usePatterns();
  return capOf('patterns', pro, patterns, useDemo().active);
}

export function useExitModelCap(): FreeTierCap {
  const { pro } = useEntitlement();
  const { exitModels } = useExitModels();
  return capOf('exitModels', pro, exitModels, useDemo().active);
}
